import React, { memo } from 'react';
import { styled as styled2 } from '@compiled/react';
import { lazyForPaint } from 'react-loosely-lazy';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { IssueSummaryFieldBoundary } from '@atlassian/jira-issue-field-summary/src/ui/async.tsx';
import type { MultilineInlineSummary as MultilineInlineSummaryType } from '@atlassian/jira-issue-field-summary/src/ui/multiline-inline-summary/index.tsx';
import { SUMMARY_TYPE } from '@atlassian/jira-platform-field-config';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics';
import SLAExperienceTracker from '../../../../sla-experience-tracker';
import { CARD_CONTAINER_SELECTOR } from '../../constants';
import { StaticSummaryField } from './summary-field-static';
import type { SummaryFieldProps } from './types';

// MultilineInlineSummary is actually just a proxy for @atlassian/jira-issue-field-summary-inline-edit-link
const MultilineInlineSummaryLazy = lazyForPaint<typeof MultilineInlineSummaryType>(() =>
	import(
		/* webpackChunkName: "async-multiline-inline-summary" */ '@atlassian/jira-issue-field-summary/src/ui/multiline-inline-summary'
	).then(({ MultilineInlineSummary }) => MultilineInlineSummary),
);

const SummaryField = ({
	shouldRenderRichCard,
	issueKey,
	highlightText,
	onSummaryEditStateChange,
	summary,
	isUserAtLeastMember,
}: SummaryFieldProps) => {
	const summaryFieldStatic = <StaticSummaryField highlightText={highlightText} summary={summary} />;

	if (shouldRenderRichCard && isUserAtLeastMember) {
		return (
			<IssueSummaryFieldBoundary packageName={BACKLOG_PACKAGE_NAME} fallback={summaryFieldStatic}>
				<SLAExperienceTracker
					experienceTarget={SUMMARY_TYPE}
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<MultilineSummaryFieldWrapper>
						<ContextualAnalyticsData attributes={{ isInlineEditing: true }}>
							<MultilineInlineSummaryLazy
								issueKey={issueKey}
								highlightText={highlightText}
								onEditStateChange={onSummaryEditStateChange}
							/>
						</ContextualAnalyticsData>
					</MultilineSummaryFieldWrapper>
				</SLAExperienceTracker>
			</IssueSummaryFieldBoundary>
		);
	}

	return summaryFieldStatic;
};

export default memo<SummaryFieldProps>(SummaryField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const MultilineSummaryFieldWrapper = styled2.div({
	marginLeft: token('space.negative.100', '-8px'),
	marginRight: token('space.negative.100', '-8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"& > :not([role='presentation'])": {
		marginLeft: token('space.0', '0px'),
		marginTop: token('space.negative.200', '16px'),
		position: 'absolute',
		width: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'form > div': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			marginTop: '0 !important',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"a[data-component-selector='issue-field-summary-inline-edit-link.ui.read.styled-link-item']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		display: () =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			ff('backlog-issue-summary-browser-large-font-size-fix_0k38w') ? 'flex' : undefined,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:hover &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:active &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus-within &`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"[data-attr='edit-summary-control']": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			display: 'flex !important',
		},
	},
});
